import { Content } from "@r8er/components/marketing/content";
import styles from "@r8er/components/marketing/content/content.module.css";
import {
  BookDemoButton,
  CtaBlock,
  Ctas,
} from "@r8er/components/marketing/ctas";
import { Hero } from "@r8er/components/marketing/hero";
import { Layout } from "@r8er/components/marketing/layout";
import { Newsletter } from "@r8er/components/marketing/newsletter";
import { getWhatHappensNext } from "@r8er/lib/cms";
import { withMiddleware } from "@r8er/lib/middleware";
import {
  CORP_INVESTOR_TYPES,
  VC_PE_INVESTOR_TYPES,
} from "@r8er/lib/procedures/authorise_user";
import { InferGetServerSidePropsType } from "next";
import Head from "next/head";

export const getServerSideProps = withMiddleware(
  async ({ req, prisma }) => {
    const whatHappensNext = await getWhatHappensNext();

    const angelInvestors =
      43 +
      (await prisma.investor_profile.count({
        where: {
          blocked: false,
          primary_user: { deactivated: false },
          company_types: { has: "angel_private_investor" },
        },
      }));
    const familyOfficeInvestors =
      9 +
      (await prisma.investor_profile.count({
        where: {
          blocked: false,
          primary_user: { deactivated: false },
          company_types: { has: "family_office" },
        },
      }));
    const vcInvestors =
      17 +
      (await prisma.investor_profile.count({
        where: {
          blocked: false,
          primary_user: { deactivated: false },
          company_types: { hasSome: VC_PE_INVESTOR_TYPES },
        },
      }));
    const corpInvestors =
      25 +
      (await prisma.investor_profile.count({
        where: {
          blocked: false,
          primary_user: { deactivated: false },
          company_types: { hasSome: CORP_INVESTOR_TYPES },
        },
      }));

    return {
      props: {
        whatHappensNext,
        counts: {
          angelInvestors,
          familyOfficeInvestors,
          vcInvestors,
          corpInvestors,
        },
      },
    };
  },
  { mode: "any" }
);

export default function Entrepreneurs({
  whatHappensNext,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  return (
    <div>
      <Head>
        <title>Entrepreneurs - R8ER</title>
        <link rel="icon" href="/favicon.png" />
      </Head>

      <Layout>
        <Hero isSlim title="Entrepreneurs" img="/entrepreneurs-hero.png" />

        <Content
          isFullWidth
          isCentered
          style={{
            paddingBottom: 0,
          }}
        >
          <p
            className="p--standard"
            style={{
              fontSize: "calc(1.65 * var(--font-size-base))",
              margin: "0 auto",
              letterSpacing: "-.25px",
            }}
          >
            For R8ER-registered entrepreneurs, our team of experts will provide
            valuable insights to ensure the best possible chance of securing
            investment. Once finalized, your company's profile will be directly
            available to R8ER investors for the period of one full year.
          </p>
        </Content>
        <Content
          isFullWidth
          isNarrow
          className={styles.topSection}
          isCentered
          style={{
            paddingTop: 0,
          }}
        >
          <BookDemoButton />
        </Content>

        <Content
          isGrey
          backgroundImage="/entrepreneurs-how.png"
          minHeight={750}
        >
          <h2 className="h2--standard">
            How can I become a<br />
            R8ER entrepreneur?
          </h2>
          <p className="p--standard">
            As part of your initial application, we will require a two minute
            video, which will provide an elevator pitch for your company. This
            should include an introduction to your business model, how it stands
            to benefit healthcare and how an investment will support its
            success.
          </p>
          <p className="p--standard">
            In addition, you will be provided with a &quot;Step-by-Step&quot;
            guide for how to submit your documentation before being reviewed by
            our R8ERs. Our application is designed to be short and succinct.
            Please refer to the FAQ section for further information.
          </p>
        </Content>

        <Content
          isRightAligned
          innerImage="/entrepreneurs-report.png"
          minHeight={750}
        >
          <h2 className="h2--standard">
            When I'm ready with my documentation,
            <br />
            what happens next?
          </h2>
          <div
            className="ghost-container"
            dangerouslySetInnerHTML={{ __html: whatHappensNext.html }}
          ></div>
        </Content>

        <Content
          isBlue
          backgroundImage="/entrepreneurs-negotiation.png"
          minHeight={750}
        >
          <h2 className="h2--standard">
            How does the negotiation
            <br />
            phase work?
          </h2>
          <p className="p--standard">
            Negotiations take place directly between investors and
            entrepreneurs, without the involvement of R8ER.
          </p>
        </Content>

        <Content
          isRightAligned
          backgroundImage="/entrepreneurs-investment.png"
          minHeight={750}
        >
          <h2 className="h2--standard">
            If I successfully secure&nbsp;
            <br />
            investment, what happens next?
          </h2>
          <p className="p--standard">
            Firstly, congratulations! As a successfully funded business, R8ER
            will remain on hand to help promote your ongoing successes, while
            potentially helping to raise further rounds of funding in the
            future.
          </p>
        </Content>

        <Ctas>
          <CtaBlock
            title="Sign Up to become a R8ER Entrepreneur"
            img="/cta-investors.png"
            ctaItems={[{ text: "Sign Up Now!", link: "/auth/signup" }]}
          />
          <CtaBlock
            title="Speak to a member of the R8ER team"
            img="/cta-contact.png"
            ctaItems={[
              { text: "Contact Us", link: "/contact" },
              {
                text: "Book a demo",
                link: "https://calendly.com/d/y5j-gmq-5x9/r8er-start-up-one-to-one-meeting",
              },
            ]}
            isLight
          />
        </Ctas>

        <Newsletter />
      </Layout>
    </div>
  );
}
